"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { platform, type DesignAIWallet, type DesignAIUsage } from "./api";
import {
  Button,
  Input,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from "./ui";
import css from "./BuildyWorkspace.module.css";

/** The server owns wallet scope and grant authority; viewing Settings never funds it. */
export function WebsiteAIWallet({
  websiteId,
  org,
  csrf,
}: {
  websiteId: string;
  org: number;
  csrf: string;
}) {
  const [wallet, setWallet] = useState<DesignAIWallet | null>(null);
  const [usage, setUsage] = useState<DesignAIUsage[]>([]);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const lock = useRef(false);
  const intent = useRef<{ signature: string; key: string } | null>(null);
  const base = `websites/${websiteId}/`;
  useEffect(() => {
    const controller = new AbortController();
    setWallet(null);
    setUsage([]);
    setLoadError("");
    let timer: ReturnType<typeof setTimeout>;
    function load() {
      void Promise.all([
        platform<DesignAIWallet>(base + "ai-wallet/", {
          org,
          signal: controller.signal,
        }),
        platform<DesignAIUsage[]>(base + "ai-usage/", {
          org,
          signal: controller.signal,
        }),
      ])
        .then(([next, receipts]) => {
          if (!controller.signal.aborted) {
            setWallet(next);
            setUsage(receipts);
            setLoadError("");
          }
        })
        .catch((cause) => {
          if (!controller.signal.aborted)
            setLoadError(
              cause instanceof Error
                ? cause.message
                : "Could not load website AI credits.",
            );
        })
        .finally(() => {
          if (!controller.signal.aborted) timer = setTimeout(load, 5000);
        });
    }
    load();
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [base, org, retry]);
  async function grant(event: FormEvent) {
    event.preventDefault();
    if (lock.current || !wallet?.can_grant) return;
    const count = Number(amount);
    if (
      !Number.isInteger(count) ||
      count < 1 ||
      count > 2147483647 ||
      !reason.trim()
    )
      return;
    const body = { amount: count, reason: reason.trim() };
    const signature = JSON.stringify(body);
    if (intent.current?.signature !== signature)
      intent.current = { signature, key: crypto.randomUUID() };
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      const next = await platform<DesignAIWallet>(base + "ai-wallet/grant/", {
        method: "POST",
        org,
        csrf,
        body: { ...body, request_key: intent.current.key },
      });
      setWallet(next);
      intent.current = null;
      setAmount("");
      setReason("");
      setOpen(false);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not add website AI credits.",
      );
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return (
    <section className={css.panel} aria-label="Website AI credits">
      <div className={css.panelHeading}>
        <h2>Website AI credits</h2>
        {wallet?.can_grant && (
          <Button
            variant="secondary"
            onClick={() => {
              setError("");
              setOpen(true);
            }}
          >
            Add credits
          </Button>
        )}
      </div>
      <div className={css.panelBody}>
        {!wallet && !loadError && <p role="status">Loading credits…</p>}
        {wallet && (
          <>
            <dl className={css.details}>
              <div>
                <dt>Available</dt>
                <dd>
                  {wallet.unlimited
                    ? "Unlimited agency access"
                    : `${wallet.available} credit${wallet.available === 1 ? "" : "s"}`}
                </dd>
              </div>
              <div>
                <dt>Per completed AI change</dt>
                <dd>
                  {wallet.credits_per_change} credit
                  {wallet.credits_per_change === 1 ? "" : "s"}
                </dd>
              </div>
              <div>
                <dt>Reserved</dt>
                <dd>
                  {wallet.reserved} credit{wallet.reserved === 1 ? "" : "s"}
                </dd>
              </div>
            </dl>
            <p>
              Manual edits are free. Failed or cancelled AI changes return their
              reserved credits.
            </p>
            {wallet.unlimited && (
              <p>Your agency changes do not use the customer’s credits.</p>
            )}
            {usage.length > 0 && (
              <details className={css.creditHistory}>
                <summary>Recent AI changes</summary>
                <table
                  className={`${css.pagesTable} ${css.creditTable}`}
                  aria-label="Website AI credit activity"
                >
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Status</th>
                      <th scope="col">Credits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usage.map((receipt) => (
                      <tr key={receipt.change_id}>
                        <td>
                          {new Date(receipt.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          {
                            {
                              HELD: "Reserved",
                              SPENT: "Completed",
                              RELEASED: "Returned",
                            }[receipt.status]
                          }
                        </td>
                        <td>
                          {receipt.unlimited ? "Agency" : receipt.credits}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </>
        )}
        {(error || loadError) && !open && (
          <div role="alert">
            <p>{error || loadError}</p>
            <Button
              variant="secondary"
              onClick={() => setRetry((value) => value + 1)}
            >
              Try again
            </Button>
          </div>
        )}
      </div>
      {open && (
        <Dialog
          ariaLabel="Add website AI credits"
          onClose={() => {
            if (!busy) setOpen(false);
          }}
          closeOnEscape={!busy}
        >
          <DialogHeader>
            <DialogTitle>Add website AI credits</DialogTitle>
          </DialogHeader>
          <form onSubmit={grant}>
            <DialogBody>
              <label>
                Credits
                <Input
                  required
                  type="number"
                  min={1}
                  max={2147483647}
                  step={1}
                  value={amount}
                  disabled={busy}
                  onChange={(event) => setAmount(event.target.value)}
                />
              </label>
              <label>
                Reason
                <Input
                  required
                  maxLength={180}
                  value={reason}
                  disabled={busy}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <p>
                Adds credits to this website’s owning organization. This does
                not charge a payment method.
              </p>
              {error && <p role="alert">{error}</p>}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={busy || !amount || !reason.trim()}
              >
                {busy ? "Adding…" : "Add credits"}
              </Button>
            </DialogFooter>
          </form>
        </Dialog>
      )}
    </section>
  );
}
